import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import type { OrderStatusDistribution } from "@/store/analyticsApi";

interface OrderStatusChartProps {
  data: OrderStatusDistribution[];
  isLoading?: boolean;
  title?: string;
}

// Status colors
const STATUS_COLORS: Record<string, string> = {
  PENDING: "hsl(45, 93%, 47%)", // Yellow
  CONFIRMED: "hsl(221, 83%, 53%)", // Blue
  PREPARING: "hsl(262, 83%, 58%)", // Purple
  READY: "hsl(199, 89%, 48%)", // Cyan
  OUT_FOR_DELIVERY: "hsl(25, 95%, 53%)", // Orange
  DELIVERED: "hsl(142, 76%, 36%)", // Green
  CANCELLED: "hsl(0, 84%, 60%)", // Red
};

// Format status for display
const formatStatus = (status: string) => {
  return status
    .split("_")
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(" ");
};

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    payload: OrderStatusDistribution & { fill: string };
  }>;
}

const CustomTooltip = ({ active, payload }: CustomTooltipProps) => {
  if (active && payload && payload.length) {
    const item = payload[0].payload;
    return (
      <div className="bg-popover border border-border rounded-lg shadow-lg p-3">
        <div className="flex items-center gap-2 mb-2">
          <div
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: item.fill }}
          />
          <p className="text-sm font-medium text-foreground">
            {formatStatus(item.status)}
          </p>
        </div>
        <div className="space-y-1">
          <p className="text-sm">
            <span className="text-muted-foreground">Orders:</span>{" "}
            <span className="font-medium">{item.count}</span>
          </p>
          <p className="text-sm">
            <span className="text-muted-foreground">Share:</span>{" "}
            <span className="font-medium">{item.percentage}%</span>
          </p>
        </div>
      </div>
    );
  }
  return null;
};

interface CustomLegendProps {
  payload?: Array<{
    value: string;
    color: string;
  }>;
}

const CustomLegend = ({ payload }: CustomLegendProps) => {
  if (!payload) return null;

  return (
    <div className="flex flex-wrap justify-center gap-3 mt-4">
      {payload.map((entry, index) => (
        <div key={`legend-${index}`} className="flex items-center gap-1.5">
          <div
            className="w-2.5 h-2.5 rounded-full"
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-xs text-muted-foreground">
            {formatStatus(entry.value)}
          </span>
        </div>
      ))}
    </div>
  );
};

export function OrderStatusChart({
  data,
  isLoading,
  title = "Order Status",
}: OrderStatusChartProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>Distribution of order statuses</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center">
            <div className="animate-pulse text-muted-foreground">
              Loading chart...
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>Distribution of order statuses</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center text-muted-foreground">
            No data available for the selected period
          </div>
        </CardContent>
      </Card>
    );
  }

  // Prepare data with formatted names
  const chartData = data.map((item) => ({
    ...item,
    name: formatStatus(item.status),
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>Distribution of order statuses</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={2}
                dataKey="count"
                nameKey="status"
                label={(props) => {
                  const percentage = (props.payload as OrderStatusDistribution).percentage;
                  return percentage > 5 ? `${percentage}%` : "";
                }}
                labelLine={false}
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={STATUS_COLORS[entry.status] || "hsl(var(--muted))"}
                    stroke="hsl(var(--background))"
                    strokeWidth={2}
                  />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend
                content={<CustomLegend />}
                formatter={(value) => formatStatus(value as string)}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
